import { h } from 'preact'
import { isRoomMuted, isRoomUnread } from '@tshmanager/chat-shared'
import { PresenceDot } from './PresenceDot.jsx'

/**
 * RoomItem — a single row in the room sidebar.
 *
 * Props: { room, store, cable, active, onClick }
 *
 * room shape (from API):
 *   { id, name, display_name, type, avatar_url,
 *     other_person?: { id, name, avatar_url },
 *     last_message_body, last_message_at, unread }
 */
export function RoomItem({ room, store, cable, active, onClick }) {
  const name        = room.display_name || room.name || ''
  const snippet     = (room.last_message_body || '').slice(0, 60)
  const unread      = isRoomUnread(room) ? (typeof room.unread === 'number' ? room.unread : 1) : 0
  const isDirect    = room.kind === 'direct' || room.type === 'direct'
  const otherPerson = room.other_person

  return (
    <div
      class={`chat-sidebar-room${active ? ' chat-sidebar-room--active' : ''}`}
      role="button"
      tabIndex={0}
      data-room-id={room.id}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick() }}
    >
      <div class="chat-sidebar-room-avatar-wrap">
        <RoomAvatar room={room} name={name} isDirect={isDirect} />
        {isDirect && otherPerson && (
          <PresenceDot
            personId={otherPerson.id}
            store={store}
            size="sm"
          />
        )}
      </div>

      <div class="chat-sidebar-room-body">
        <div class="chat-sidebar-room-name-row">
          <span class="chat-sidebar-room-name">{name}</span>
          {isRoomMuted(room.involvement) && (
            <i class="bi bi-bell-slash chat-sidebar-room-muted-icon" title="" aria-hidden="true" />
          )}
          {unread > 0 && (
            <span class="chat-sidebar-room-badge">{unread > 99 ? '99+' : unread}</span>
          )}
        </div>
        {snippet && (
          <div class="chat-sidebar-room-snippet">{snippet}</div>
        )}
      </div>
    </div>
  )
}

function RoomAvatar({ room, name, isDirect }) {
  // For direct rooms the room itself has no avatar — use the other person's photo.
  const avatarUrl = room.avatar_url || (isDirect ? room.other_person?.avatar_url : null)

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        class={`chat-room-avatar ${isDirect ? 'chat-room-avatar--direct' : 'chat-room-avatar--group'}`}
        width={36}
        height={36}
        loading="lazy"
        onLoad={e => e.target.classList.add('is-loaded')}
        onError={e => { e.target.style.display = 'none' }}
      />
    )
  }

  const initials = room.avatar_initials || name.slice(0, 1).toUpperCase() || '?'

  return (
    <div class={`chat-room-avatar chat-room-avatar--initials ${isDirect ? 'chat-room-avatar--direct' : 'chat-room-avatar--group'}`}>
      {isDirect ? initials : <i class="bi bi-people-fill" />}
    </div>
  )
}
